<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { WorkflowDefinition, WorkflowStatus } from "~/api";
import { showError } from "~/utils/handleAPIError";
import SortableStatuses from "./SortableStatuses.vue";

// @ts-expect-error - error
const localWorkflowDefinition = ref<WorkflowDefinition>({});

const props = defineProps<{
  workflowDefinition: WorkflowDefinition;
}>();

const newWorkFlowStatus = ref<WorkflowStatus>({
  code: "",
  label: "",
  isEndpoint: false,
});
const isEditing = ref(false);
const selectedStatus = ref<WorkflowStatus>();

const editStatus = (status: WorkflowStatus) => {
  isEditing.value = true;
  selectedStatus.value = { ...status };
  newWorkFlowStatus.value = { ...status };
};

const saveNeeded = computed(() => {
  return (
    newWorkFlowStatus.value.code !== selectedStatus.value?.code ||
    newWorkFlowStatus.value.label !== selectedStatus.value.label
  );
});

const resetInputs = () => {
  isEditing.value = false;
  selectedStatus.value = undefined;
  newWorkFlowStatus.value = { code: "", label: "", isEndpoint: false };
};

const removeSpaces = () => {
  if (newWorkFlowStatus.value.code) {
    newWorkFlowStatus.value.code = newWorkFlowStatus.value.code.replaceAll(
      /\s+/g,
      "",
    );
  }
};

const updateStatus = async () => {
  const { code, label, isEndpoint } = newWorkFlowStatus.value;

  if (!label) {
    showError("Label is required");
    return;
  }

  const statusList = localWorkflowDefinition.value.statusList || [];

  const isDuplicateCode = statusList.some(
    (s) =>
      s.code?.toLowerCase() === code?.toLowerCase() &&
      s.code !== selectedStatus.value?.code,
  );

  if (isDuplicateCode) {
    showError(`Status with code ${code} already exists`);
    return;
  }

  if (!code && label) {
    newWorkFlowStatus.value.code = label.replaceAll(/\s+/g, "");
  }

  if (!selectedStatus.value) {
    if (
      localWorkflowDefinition.value.statusList.some(
        (s) =>
          s.code?.toLowerCase() === newWorkFlowStatus.value.code?.toLowerCase(),
      )
    ) {
      showError(
        `Status with code "${newWorkFlowStatus.value.code}" already exists`,
      );
      return;
    }

    if (!localWorkflowDefinition.value.statusList) {
      localWorkflowDefinition.value.statusList = [];
    }
    localWorkflowDefinition.value.statusList.push({
      code: newWorkFlowStatus.value.code,
      label: label,
      isEndpoint: isEndpoint,
    });
  } else {
    const statusIndex = localWorkflowDefinition.value.statusList?.findIndex(
      (s) => s.code === selectedStatus.value?.code,
    );
    if (statusIndex !== -1) {
      localWorkflowDefinition.value.statusList[statusIndex] = {
        ...newWorkFlowStatus.value,
      };
    }
  }
  resetInputs();
};

const deleteStatus = () => {
  const statusList = localWorkflowDefinition.value.statusList;

  const statusIndex = statusList.findIndex(
    (s) => s.code === selectedStatus.value?.code,
  );

  if (statusIndex !== -1) {
    statusList.splice(statusIndex, 1);
    localWorkflowDefinition.value.statusList = statusList;
    resetInputs();
  }
};

onMounted(() => {
  localWorkflowDefinition.value = props.workflowDefinition;
});
</script>

<template>
  <div class="wfd-statuses">
    <div class="status-editor">
      Add
      <el-input
        v-model="newWorkFlowStatus.label"
        draggable
        size="large"
        placeholder="Display Label"
        @keyup.enter="updateStatus"
      >
      </el-input>
      <el-input
        v-model="newWorkFlowStatus.code"
        draggable
        size="large"
        placeholder="Code"
        @input="removeSpaces"
        @keyup.enter="updateStatus()"
      >
      </el-input>
    </div>
    <div class="status-editor">
      <el-button
        :disabled="
          !((newWorkFlowStatus.code || newWorkFlowStatus.label) && saveNeeded)
        "
        @click="updateStatus"
      >
        {{ isEditing ? "Save" : "Add" }}
      </el-button>
      <el-button v-if="isEditing" plain type="danger" @click="resetInputs"
        >Cancel</el-button
      >

      <el-button
        v-if="isEditing && localWorkflowDefinition.statusList.length > 1"
        type="danger"
        @click="deleteStatus"
      >
        Delete
      </el-button>
    </div>

    <SortableStatuses
      v-if="localWorkflowDefinition.statusList"
      :key="localWorkflowDefinition.statusList?.length"
      class="sortable-statuses"
      :status-list="localWorkflowDefinition.statusList"
      @editing-status="editStatus"
    />
  </div>
</template>

<style lang="less">
.wfd-statuses {
  display: grid;
  grid-template-columns: auto 100px 300px;
  grid-template-areas:
    "statuses statuses statuses"
    "input add save";
  gap: 1em;
  align-items: center;
  user-select: none;

  .status-editor {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  .add {
    grid-area: add;
  }
  .save {
    grid-area: save;
  }
  .sortable-statuses {
    grid-area: statuses;
  }
}
</style>
